import { useEffect, useMemo, useState } from 'react'

import { logger } from '~/debug/logger'
import { usePrincipalOrgId } from '~/store/slices/currentOrg'

import type { GeneratorModel } from '../client/ders'
import { useGetGeneratorsQuery } from '../client/ders'
import { combineSkip } from '../hooks/utils'
import type { IndexedDbService } from './storage/IndexedDbService'
import type { GeneratorCacheRecord } from './storage/schema'

const GENERATORS_PAGE_SIZE = 1000

export type GeneratorsDataManagerContextValue = {
  data: GeneratorModel[]
  isLoading: boolean
  isFetching: boolean
}

function useGeneratorsQuery(options?: { skip?: boolean }) {
  const principalOrgId = usePrincipalOrgId()

  const { data, isLoading, isFetching } = useGetGeneratorsQuery(
    {
      orgIds: principalOrgId ? [principalOrgId] : undefined,
      orgDepth: 100,
      includeLocalManaged: true,
      page: 0,
      count: GENERATORS_PAGE_SIZE
    },
    combineSkip(!principalOrgId, options)
  )

  return {
    generators: data?.data,
    isLoading,
    isFetching
  }
}

export default function useGeneratorsDataManager(service: IndexedDbService) {
  const { generators, isLoading, isFetching } = useGeneratorsQuery()

  const data = useGeneratorsPersistence(service, generators)

  return useMemo<GeneratorsDataManagerContextValue>(
    () => ({
      data,
      isLoading,
      isFetching
    }),
    [data, isLoading, isFetching]
  )
}

function useGeneratorsPersistence(service: IndexedDbService, generators?: GeneratorModel[]): GeneratorModel[] {
  const [hydratedGenerators, setHydratedGenerators] = useState<GeneratorModel[]>([])

  useEffect(() => {
    async function hydrateGeneratorsCache() {
      try {
        const records = await service.getAll('generators')
        setHydratedGenerators(records.map((record) => record.payload))
      } catch (error) {
        logger.error('Failed to hydrate generators cache', error)
      }
    }

    void hydrateGeneratorsCache()
  }, [service])

  useEffect(() => {
    if (!generators) return

    const fetchedAt = Date.now()
    const records: GeneratorCacheRecord[] = generators.map((generator) => ({
      id: generator.id,
      payload: generator,
      syncedAt: fetchedAt
    }))

    const replaceRecords = async () => {
      try {
        await service.replace('generators', records)
      } catch (error) {
        logger.error('Failed to persist generators snapshot', error)
      }
    }

    void replaceRecords()
  }, [generators, service])

  return generators ?? hydratedGenerators
}
